import {
  AuthContextProvider,
  DataContextProvider,
  IRouterCommonProps,
} from "@contexts";
import { OptimizelyContextProvider } from "@contexts/optimizely";
import React, { Fragment, useMemo } from "react";
import {
  Outlet,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
  createHashRouter,
  createMemoryRouter,
} from "react-router-dom";

export interface IReactRouterProps extends IRouterCommonProps<RouteObject> {
  type: "browser" | "memory" | "hash";
}

const RouterContents = ({
  useAuthProvider,
  dataProviders,
  LoadingComponent,
  outletWrapper,
}: Pick<
  IReactRouterProps,
  "useAuthProvider" | "dataProviders" | "LoadingComponent" | "outletWrapper"
>) => {
  const authProvider = useAuthProvider();

  if (authProvider?.isLoading()) {
    return <LoadingComponent />;
  }

  const OutletWrapper = outletWrapper || Fragment;

  return (
    <AuthContextProvider
      {...(authProvider ?? {})}
      isProvided={Boolean(authProvider)}
    >
      <OptimizelyContextProvider>
        <DataContextProvider providers={dataProviders}>
          <OutletWrapper>
            <Outlet />
          </OutletWrapper>
        </DataContextProvider>
      </OptimizelyContextProvider>
    </AuthContextProvider>
  );
};

const getRouterChildren = ({
  AuthProvider,
  ...rest
}: Pick<
  IReactRouterProps,
  | "AuthProvider"
  | "useAuthProvider"
  | "dataProviders"
  | "LoadingComponent"
  | "outletWrapper"
>) => {
  if (AuthProvider) {
    return (
      <AuthProvider>
        <RouterContents {...rest} />
      </AuthProvider>
    );
  }

  return <RouterContents {...rest} />;
};

export const ReactRouter = ({
  type,
  routes,
  useAuthProvider,
  AuthProvider,
  LoadingComponent,
  dataProviders,
  outletWrapper,
}: IReactRouterProps) => {
  const Loading = useMemo(
    () => LoadingComponent || <div>Loading...</div>,
    [LoadingComponent],
  );
  const router = useMemo(
    () =>
      (type === "browser"
        ? createBrowserRouter
        : type === "memory"
          ? createMemoryRouter
          : createHashRouter)([
        {
          element: getRouterChildren({
            useAuthProvider,
            AuthProvider,
            LoadingComponent: Loading,
            dataProviders,
            outletWrapper,
          }),
          children: routes,
        },
      ]),
    [
      type,
      routes,
      useAuthProvider,
      AuthProvider,
      Loading,
      dataProviders,
      outletWrapper,
    ],
  );
  return <RouterProvider router={router} fallbackElement={<Loading />} />;
};